/* eslint-disable react/no-unstable-nested-components --
 * CsvDiffTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 CsvDiffTable は戻り値を <TableCell> の
 * children として表示するだけで独立した component subtree を持たないため、
 * lint 警告の前提 (= subtree 破棄) に該当しない
 * (AdminSingularDay/components/SingularDayTable.tsx と同判断)。
 */
import { useRef, useState } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import {
  type ImportCisContractsRowResult,
  ImportCisContractsRowStatus,
} from '@ebalance/proto/bffuser/v1/cis_contract_model_pb'
import { type ImportCisContractsResponse } from '@ebalance/proto/bffuser/v1/cis_contract_service_pb'
import { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Alert,
  Box,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CsvDiffLoading } from '~/components/common/CsvUpload/CsvDiffLoading'
import { CsvDiffPanel } from '~/components/common/CsvUpload/CsvDiffPanel'
import {
  CsvDiffErrorText,
  CsvDiffTable,
  type CsvDiffTableColumn,
  type CsvDiffTone,
  CsvDiffValueChange,
  csvDiffBadgeSpec,
} from '~/components/common/CsvUpload/CsvDiffTable'
import { CsvUploadFileInfo } from '~/components/common/CsvUpload/CsvUploadFileInfo'
import { CsvUploadNoopBanner } from '~/components/common/CsvUpload/CsvUploadNoopBanner'
import { CsvUploadPanelFooter } from '~/components/common/CsvUpload/CsvUploadPanel'
import { CsvUploadStep1Panel } from '~/components/common/CsvUpload/CsvUploadStep1Panel'
import { Dropzone } from '~/components/common/CsvUpload/Dropzone'
import { Stepper } from '~/components/common/CsvUpload/Stepper'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { useCsvImportReturn } from '~/hooks/useCsvImportReturn'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { downloadCsv } from '~/libs/downloadCsv'
import { convertAreaLabel } from '~/models/area'
import { cisContractCsvTemplateFileName } from '~/models/cisContractCsvHeader'
import { now as serverNow } from '~/models/clock'
import { decodeCsvFile } from '~/models/csvRecords'
import { resolveCsvUploadConfirmGate } from '~/models/csvUploadConfirmGate'
import { formatJstTimeHm } from '~/models/dateTime'
import {
  type SummaryCardFilter,
  SummaryCards,
} from '~/pages/CisContractCsvUpload/components/SummaryCards'
import { useImportCisContracts } from '~/pages/CisContractCsvUpload/hooks/useImportCisContracts'
import {
  MAX_FILE_SIZE_BYTES,
  type PrecheckErrorKey,
  precheckCisContractCsv,
} from '~/pages/CisContractCsvUpload/models/cisContractCsv'
import {
  buildCisContractCsvTemplate,
  cisContractCsvColumnGuide,
  cisContractCsvSampleRow,
} from '~/pages/CisContractCsvUpload/models/cisContractCsvColumnGuide'
import { routePaths } from '~/routes/routePaths'

type FileInfo = {
  name: string
  size: number
  selectedAt: Date
  /** RPC に送る CSV 生バイト (backend がパースする)。 */
  content: Uint8Array
}

/** AC-DUI-011: 削除 100 行以上で警告バナー。 */
const DELETE_WARNING_THRESHOLD = 100
/** プレビュー表の描画上限行数。 */
export const PREVIEW_DISPLAY_LIMIT = 1000

/** バイト数を "38 KB" のような概算表示に変換する。 */
/**
 * Step1 の前段チェックのエラーを i18n キーにする。囲みの不正 (AC-DUC-045) は
 * 画面とサーバーで同じ文言を求めるので他の取込画面と共通のキーを引き、読み込み
 * 自体の失敗は DropZone と同じ汎用の文言にする。それ以外はこの画面の文言。
 */
function precheckMessageKey(key: PrecheckErrorKey): string {
  switch (key) {
    case 'quoteInvalid':
      return 'common:upload.error.quoteInvalid'
    case 'readFailed':
      return 'common:upload.error.generic'
    case 'noDataRows':
      return 'common:upload.error.noDataRows'
    default:
      return `page:cisContractCsvUpload.step1.precheck.${key}`
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/** google.type.Date ライクな値を "YYYY/MM/DD" に整形する (timezone 変換を挟まない)。 */
function formatDate(d?: { year: number; month: number; day: number }): string {
  if (d === undefined) return ''
  return `${d.year}/${String(d.month).padStart(2, '0')}/${String(d.day).padStart(2, '0')}`
}

/**
 * 契約終了日未定のセンチネル (spec L83: 空欄 = 9999/12/31)。backend は常に
 * 9999-12-31 に正規化して返すので、表示側で「現在も有効」に読み替える。
 */
function isOpenEndDate(d?: {
  year: number
  month: number
  day: number
}): boolean {
  return d !== undefined && d.year === 9999 && d.month === 12 && d.day === 31
}

/**
 * VoltageClass → 日本語ラベル。エリアと違い canonical helper (`models/area` の
 * convertAreaLabel 相当) がまだ無く、既存画面も page namespace にラベルを置いて
 * いるため、本画面も page-local に持つ (横断ヘルパー化は別 concern)。
 */
function voltageClassLabel(
  v: VoltageClass,
  t: (key: string) => string,
): string {
  switch (v) {
    case VoltageClass.EXTRA_HIGH:
      return t('page:cisContractCsvUpload.voltageClass.extraHigh')
    case VoltageClass.HIGH:
      return t('page:cisContractCsvUpload.voltageClass.high')
    case VoltageClass.LOW:
      return t('page:cisContractCsvUpload.voltageClass.low')
    default:
      return ''
  }
}

/**
 * 差分「変更」の列を「変更前 → 変更後」で、「削除」の列を既存値で描く
 * (common.md「変更行は 変更前 → 変更後 をインラインで表示」「削除行は削除対象の
 * 既存値をインライン表示」)。誤上書きはこの表示で防ぐ設計なので、値が変わる列は
 * すべて遷移を出す (2026-08-25 codex レビュー指摘: 旧実装は existing が空文字の
 * ときに遷移を出さず、未設定→登録の変更が新値だけに見えていた)。
 *
 * `existing` が空文字 (= 未設定) のまま値が入った行は、`unsetLabel` を渡せば
 * 「{unsetLabel} → {next}」の形で描く。呼び出し側の i18n 文言をハードコード
 * しないため、汎用関数の本体には「未設定」等の表示文言を持たない — 呼び出し側
 * (owner_business_code 列) が t() で解決した文言をこの引数に渡す。`unsetLabel`
 * を渡さない列 (area / tariffCode / voltageClass はいずれも必須列で existing が
 * 空文字になり得ない) は既存の挙動のまま (existing が空文字なら遷移を出さず
 * next のみ)。
 */
function transitionParts(
  row: Pick<ImportCisContractsRowResult, 'status'>,
  existing: string,
  next: string,
  unsetLabel?: string,
): { before?: string; after: string } {
  if (row.status === ImportCisContractsRowStatus.DELETED) {
    return { after: existing }
  }
  if (row.status !== ImportCisContractsRowStatus.CHANGED || existing === next) {
    return { after: next }
  }
  const existingDisplay = existing === '' ? unsetLabel : existing
  return existingDisplay === undefined
    ? { after: next }
    : { before: existingDisplay, after: next }
}

export function transitionLabel(
  row: Pick<ImportCisContractsRowResult, 'status'>,
  existing: string,
  next: string,
  unsetLabel?: string,
): string {
  const { before, after } = transitionParts(row, existing, next, unsetLabel)
  return before === undefined ? after : `${before} → ${after}`
}

/**
 * `transitionLabel` と同じ判定で、 差分表のセルに描く形 (変更前は取り消し線・変更後は
 * semibold。 design `.up-cell-old / .up-cell-new`) を返す。
 */
function transitionCell(
  row: Pick<ImportCisContractsRowResult, 'status'>,
  existing: string,
  next: string,
  unsetLabel?: string,
): React.ReactNode {
  const { before, after } = transitionParts(row, existing, next, unsetLabel)
  return before === undefined ? (
    after
  ) : (
    <CsvDiffValueChange after={after} before={before} />
  )
}

/**
 * サーバー起因の失敗を画面に出す文言へ変換する。
 *
 * `ConnectError.message` は connect-web が必ず `[code] <文言>` の形に組み立てる
 * (connect-error.js の createMessage)。そのまま出すと backend の指定文言と
 * 一致しないため、前置の無い `rawMessage` を使う。同じ理由で `e.message !== ''`
 * による fallback 判定は成立しない — ConnectError の message は文言が空でも
 * `[code]` になるので常に非空になり、fallback へ到達しない。
 *
 * InvalidArgument だけを「そのまま見せてよい文言」として扱う。backend が
 * ユーザー向けに組み立てた日本語 (ヘッダー違反 AC-DUI-001 / 行数上限) は
 * この code で返り、行エラーは 2xx で返るため、
 * InvalidArgument = 業務的な拒否 が成立する。それ以外 (unavailable / internal 等)
 * は技術的失敗で `Failed to fetch` のような英語が出るため定型文に寄せる。
 */
export function serverErrorText(
  e: unknown,
  t: (key: string) => string,
  fallbackKey = 'page:cisContractCsvUpload.serverError',
): string {
  if (e instanceof ConnectError && e.code === Code.InvalidArgument) {
    return e.rawMessage
  }
  return t(fallbackKey)
}

/** 行ステータス → 差分表の行の種類 (行の地色・バッジの色を決める)。 */
const ROW_STATUS_TONE: Record<
  ImportCisContractsRowStatus,
  CsvDiffTone | undefined
> = {
  [ImportCisContractsRowStatus.UNSPECIFIED]: undefined,
  [ImportCisContractsRowStatus.NEW]: 'new',
  [ImportCisContractsRowStatus.CHANGED]: 'changed',
  [ImportCisContractsRowStatus.UNCHANGED]: 'unchanged',
  [ImportCisContractsRowStatus.DELETED]: 'deleted',
  [ImportCisContractsRowStatus.ERROR]: 'error',
}

/**
 * 行ステータス → 状態バッジ表示仕様。 色は
 * `ROW_STATUS_TONE` と同じ写像から引く。 以前はバッジでなく文字色だけで、
 * 削除・エラーを赤字、 それ以外を黒字で出していた。
 */
const ROW_STATUS_BADGE: Record<ImportCisContractsRowStatus, EnumBadgeSpec> = {
  [ImportCisContractsRowStatus.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: 'page:cisContractCsvUpload.step2.status.unspecified',
  },
  [ImportCisContractsRowStatus.NEW]: csvDiffBadgeSpec(
    'new',
    'page:cisContractCsvUpload.step2.status.new',
  ),
  [ImportCisContractsRowStatus.CHANGED]: csvDiffBadgeSpec(
    'changed',
    'page:cisContractCsvUpload.step2.status.changed',
  ),
  [ImportCisContractsRowStatus.UNCHANGED]: csvDiffBadgeSpec(
    'unchanged',
    'page:cisContractCsvUpload.step2.status.unchanged',
  ),
  [ImportCisContractsRowStatus.DELETED]: csvDiffBadgeSpec(
    'deleted',
    'page:cisContractCsvUpload.step2.status.deleted',
  ),
  [ImportCisContractsRowStatus.ERROR]: csvDiffBadgeSpec(
    'error',
    'page:cisContractCsvUpload.step2.status.error',
  ),
}

/** サマリカードのフィルタ選択を row_results に適用する。 */
export function filterRows(
  rows: ImportCisContractsRowResult[],
  filter: SummaryCardFilter,
): ImportCisContractsRowResult[] {
  switch (filter) {
    case 'new':
      return rows.filter((r) => r.status === ImportCisContractsRowStatus.NEW)
    case 'changed':
      return rows.filter(
        (r) => r.status === ImportCisContractsRowStatus.CHANGED,
      )
    case 'deleted':
      return rows.filter(
        (r) => r.status === ImportCisContractsRowStatus.DELETED,
      )
    case 'error':
      return rows.filter((r) => r.status === ImportCisContractsRowStatus.ERROR)
    case 'diff':
      // AC-DUC-012「差分のみ」 = 新規 + 変更 + 削除 (据え置き・エラーは含めない)。
      return rows.filter(
        (r) =>
          r.status === ImportCisContractsRowStatus.NEW ||
          r.status === ImportCisContractsRowStatus.CHANGED ||
          r.status === ImportCisContractsRowStatus.DELETED,
      )
    case 'unchanged':
      // 据え置き行は row_results に含まれない (レスポンスサイズ対策。件数は
      // summary.unchangedRowCount が SoT)。表としては空になる。
      return []
    case 'all':
    default:
      return rows
  }
}

/**
 * プレビュー表に描画する行を選ぶ。上限以内は全行そのまま。超過時はエラー行を
 * 優先した上で先頭 PREVIEW_DISPLAY_LIMIT 行に切り詰める (RPC は 10 万行を
 * 受け付けるが、全行を DOM 化するとブラウザが長時間ブロックするため)。
 * 件数の SoT はサマリ側で、表は確認用の抜粋 (打ち切り時はバナーで明示する)。
 */
export function selectPreviewDisplayRows(
  rows: ImportCisContractsRowResult[],
): ImportCisContractsRowResult[] {
  if (rows.length <= PREVIEW_DISPLAY_LIMIT) return rows
  const errorRows = rows.filter(
    (r) => r.status === ImportCisContractsRowStatus.ERROR,
  )
  const normalRows = rows.filter(
    (r) => r.status !== ImportCisContractsRowStatus.ERROR,
  )
  return [...errorRows, ...normalRows].slice(0, PREVIEW_DISPLAY_LIMIT)
}

/**
 * CIS契約マスタ CSV アップロード画面
 * (spec `docs/specs/admin/data/uploads/cis-contract-master.md`)。
 *
 * システム管理 > CSVアップロード 配下の 2 ステップウィザード
 * (Step1 ファイル選択 → Step2 差分プレビュー → 確定モーダル)。
 * 48点データの `Supply48DataCsvUpload` と同構造。
 *
 * CSV のパース・厳格モード検証・差分判定 (新規/変更/据え置き/削除) は
 * ImportCisContracts RPC (backend) が行い、このページは生バイトを送って
 * 結果を表示する。
 * frontend 側は Step1 の軽い前段チェックのみ持つ (common.md の二重化の前段)。
 */
export const CisContractCsvUploadPage = (): React.ReactElement => {
  const showSuccessToast = useSuccessToast()
  const csvReturn = useCsvImportReturn('cisContract')
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  // 記入ガイド表・記入例案内は英語表示で英語にする (spec §1.5、#8673)
  const isEnglish = i18n.language.startsWith('en')
  const importMutation = useImportCisContracts()

  const [step, setStep] = useState<1 | 2>(1)
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [precheckError, setPrecheckError] = useState<
    | { key: PrecheckErrorKey; params?: Record<string, number | string> }
    | undefined
  >(undefined)
  // 確定の確認の段階。'activeDelete' は AC-DUI-020 の確認ダイアログを確定モーダル
  // ('commit') の前面に重ねている状態で、背面の確定モーダルも開いたまま。
  const [confirmStep, setConfirmStep] = useState<
    'closed' | 'commit' | 'activeDelete'
  >('closed')
  const showConfirmModal = confirmStep !== 'closed'
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] =
    useState<ImportCisContractsResponse | null>(null)
  const [serverErrorMessage, setServerErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [confirmErrorMessage, setConfirmErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [activeFilter, setActiveFilter] = useState<SummaryCardFilter>('all')
  // dry_run リクエストの世代番号。プレビュー通信中に Step1 へ戻って別 CSV を
  // 選び直した場合、遅延して届いた古い応答を previewResult に反映すると
  // 「表示は CSV A の差分・確定対象は CSV B」に食い違う。ファイル選択・戻る・
  // リセットで世代を進め、一致する応答のみ反映する。
  const previewGenerationRef = useRef(0)
  // ファイル読込は非同期のため、読込中に別ファイルを選び直すと古い読込結果が
  // 後着して最新選択を上書きしうる。連番で最新の読込だけを state に反映する。
  const readSeqRef = useRef(0)

  const summary = previewResult?.summary
  const hasRowError = (summary?.errorRowCount ?? 0) > 0
  const deleteCount = summary?.deletedRowCount ?? 0
  // 契約期間中の削除件数 (AC-DUI-019 / 020)。判定は backend (チェックを行った日
  // 〈日本時間〉との比較) が行い、ここでは表示するだけ。
  const activeDeleteCount = summary?.activeContractDeletedRowCount ?? 0
  const confirmGate = resolveCsvUploadConfirmGate(summary, hasRowError)
  // spec §2.5 決定 B により削除件数の専用上限は無い (行数上限 100,000 行の範囲で
  // 削除できる) ため、frontend 側に削除件数の上限判定は置かない。
  const canOpenConfirm = !isPreviewLoading && confirmGate.canOpenConfirm
  const { showNoRowsToProcessBanner } = confirmGate

  const handleFileSelected = (file: File): void => {
    previewGenerationRef.current += 1
    // 世代番号は「選択された」時点で必ず進める。拒否する選択 (サイズ超過等) でも
    // 進めないと、先行して走っている読込が完了した時に seq 一致で通過し、旧ファイルの
    // fileInfo が復活して「エラー表示のまま旧ファイルを確定できる」状態になる。
    const seq = ++readSeqRef.current
    // 読込完了までは旧ファイルの選択情報・プレビューを残さない。残すと読込中に
    // 旧内容のままプレビューへ進めてしまい、「表示は新ファイル・送信は旧ファイル」
    // のスナップショット不一致が起きる。
    setFileInfo(null)
    setPrecheckError(undefined)
    setPreviewResult(null)
    setConfirmErrorMessage(undefined)
    // サイズ上限は読み込み**前**に見る。decodeCsvFile は arrayBuffer() で全体を
    // メモリに載せるので、後で判定すると数百 MB〜GB の誤選択でタブが落ちる。
    // File.size は読み込み不要で取れる。
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setPrecheckError({ key: 'fileSizeExceeded' })
      return
    }
    // common.md §1「文字コードの判定」: 復号した文字列で前段チェックを行い、サーバーへは元の
    // バイト列を送る (サーバーも同じ順で判定する)。
    void decodeCsvFile(file).then((decoded) => {
      if (seq !== readSeqRef.current) return
      // 読み込み自体の失敗は文字コードの問題ではないので汎用の文言にする
      // (DropZone と同じ区別。common.md: 文字コード判定不能は Step1 で拒否)。
      if (decoded.kind === 'read') {
        setPrecheckError({ key: 'readFailed' })
        return
      }
      if (decoded.kind === 'encoding') {
        setPrecheckError({ key: 'notUtf8' })
        return
      }
      const result = precheckCisContractCsv(decoded.text)
      if (!result.ok) {
        setPrecheckError({ key: result.messageKey, params: result.params })
        return
      }
      setFileInfo({
        name: file.name,
        size: file.size,
        selectedAt: serverNow(),
        content: decoded.bytes,
      })
    })
  }

  /**
   * 雛形 DL: 8 列形式 (事業者コード付き、2026-08-25 PdM 確定、issue #6105) の
   * ヘッダー行 + 記入例3行 (common.md AC-DUC-031、spec §1「雛形の記入例」)。
   * ファイル名は common.md 第6節の命名規則 (`{データ種別名}_テンプレート.csv`、
   * issue #6467)。英語表示ではファイル名・ヘッダー行・ダミー値を英語で出す (common.md §6)。
   */
  const handleTemplateDownload = (): void => {
    downloadCsv(
      cisContractCsvTemplateFileName(isEnglish),
      buildCisContractCsvTemplate(isEnglish),
    )
  }

  const runPreview = (): void => {
    if (fileInfo === null) return
    const generation = previewGenerationRef.current
    setStep(2)
    setIsPreviewLoading(true)
    setServerErrorMessage(undefined)
    setActiveFilter('all')
    importMutation
      .mutateAsync({
        content: fileInfo.content,
        dryRun: true,
        csvFileName: fileInfo.name,
      })
      .then((resp) => {
        if (generation !== previewGenerationRef.current) return // 古い応答は破棄
        setPreviewResult(resp)
        setIsPreviewLoading(false)
      })
      .catch((e: unknown) => {
        if (generation !== previewGenerationRef.current) return
        // ヘッダー違反・行数上限は backend が InvalidArgument で
        // 返す (行単位でなくファイル全体の拒否)。文言はサーバー生成のものを出す。
        // チェック中の通信エラーは確定時と別の定型文 (common.md §4)。
        setServerErrorMessage(
          serverErrorText(e, t, 'common:upload.error.generic'),
        )
        setIsPreviewLoading(false)
      })
  }

  const handleBackToStep1 = (): void => {
    previewGenerationRef.current += 1
    setStep(1)
    setPreviewResult(null)
    setIsPreviewLoading(false)
  }

  const handleCommit = (): void => {
    if (fileInfo === null) return
    setConfirmErrorMessage(undefined)
    // 確定応答にも世代番号を持たせる。確定中でもダイアログのキャンセルと
    // 「ファイル選択に戻る」は押せるため、CSV A の確定中に Step1 へ戻って CSV B を
    // 選び直せる。ガードが無いと A の応答が B の操作中に画面を移したり
    // (returnAfterApply)、B の画面に A の差分を出したり (setPreviewResult) する。
    const generation = previewGenerationRef.current
    importMutation
      .mutateAsync({
        content: fileInfo.content,
        dryRun: false,
        csvFileName: fileInfo.name,
      })
      .then((resp) => {
        const isCurrent = generation === previewGenerationRef.current
        if (resp.applied) {
          // 反映は完了しているので、画面を離れていても必ず知らせる。黙って捨てると
          // 反映済みの CSV をユーザーが再投入しかねない。
          showSuccessToast({
            title: t('page:cisContractCsvUpload.successToast'),
          })
          if (isCurrent) {
            setConfirmStep('closed')
            const applied = resp.summary ?? summary
            csvReturn.returnAfterApply(
              applied === undefined
                ? undefined
                : {
                    created: applied.newRowCount,
                    updated: applied.changedRowCount,
                    deleted: applied.deletedRowCount,
                  },
            )
          }
          return
        }
        // 確定時再検証で新たな行エラー → 反映せず Step2 のエラー表示。
        // 古い応答なら現在の画面状態を壊さないよう破棄する。
        if (!isCurrent) return
        setConfirmStep('closed')
        setPreviewResult(resp)
      })
      .catch((e: unknown) => {
        if (generation !== previewGenerationRef.current) return
        const message = serverErrorText(e, t)
        // ファイル全体の業務バリデーション拒否は同じ CSV の再送では解消しない。
        // 通信・サーバー失敗はモーダルに残し、開き直さず再試行できるようにする。
        if (e instanceof ConnectError && e.code === Code.InvalidArgument) {
          setConfirmStep('closed')
          setServerErrorMessage(message)
        } else {
          setConfirmErrorMessage(message)
        }
      })
  }

  const allRows = previewResult?.rowResults ?? []
  const filteredRows = filterRows(allRows, activeFilter)

  // アップロード済みファイル情報 (ファイル名 / サイズ / 選択時刻)。 common.md
  // 「アップロード済みファイル情報の表示 (Step2 / Step3 共通)」に従い Step1 /
  // Step2 の両方に常時表示する (AC-DUC-005 / AC-DUC-006)。
  const fileInfoBox = fileInfo !== null && (
    <CsvUploadFileInfo
      meta={`${formatFileSize(fileInfo.size)} ・ ${t(
        'page:cisContractCsvUpload.fileInfo.selectedAt',
        { time: formatJstTimeHm(fileInfo.selectedAt) },
      )}`}
      name={fileInfo.name}
    />
  )

  /** 期間セル: 削除行は既存の期間、変更行は 既存 → 新 の遷移を出す。 */
  const periodCell = (row: ImportCisContractsRowResult): React.ReactNode => {
    const start = formatDate(row.startDate)
    const end = isOpenEndDate(row.endDate)
      ? t('page:cisContractCsvUpload.step2.ongoing')
      : formatDate(row.endDate)
    if (row.status === ImportCisContractsRowStatus.DELETED) {
      const existingEnd = isOpenEndDate(row.existingEndDate)
        ? t('page:cisContractCsvUpload.step2.ongoing')
        : formatDate(row.existingEndDate)
      return `${start} 〜 ${existingEnd}`
    }
    if (
      row.status === ImportCisContractsRowStatus.CHANGED &&
      row.existingEndDate !== undefined
    ) {
      const existingEnd = isOpenEndDate(row.existingEndDate)
        ? t('page:cisContractCsvUpload.step2.ongoing')
        : formatDate(row.existingEndDate)
      if (existingEnd !== end) {
        return (
          <>
            {`${start} 〜 `}
            <CsvDiffValueChange after={end} before={existingEnd} />
          </>
        )
      }
    }
    return `${start} 〜 ${end}`
  }

  const columns: Array<CsvDiffTableColumn<ImportCisContractsRowResult>> = [
    // ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    {
      key: 'status',
      header: t('page:cisContractCsvUpload.step2.columns.status'),
      cell: (row) => (
        <EnumStatusBadge map={ROW_STATUS_BADGE} value={row.status} />
      ),
    },
    {
      key: 'rowNumber',
      header: t('page:cisContractCsvUpload.step2.columns.rowNumber'),
      align: 'right',
      cell: (row) => row.rowNumber,
    },
    {
      key: 'supplyPoint',
      header: t('page:cisContractCsvUpload.step2.columns.supplyPoint'),
      // 22 桁の数字列。 等幅にしないと桁の読み比べができない (design と同じ扱い)
      mono: true,
      cell: (row) => row.supplyPointSpecificNumber,
    },
    // エリア・電圧階級は固定の区分値なので中央揃え (design `supply-point-master.html` の列揃え)
    {
      key: 'area',
      header: t('page:cisContractCsvUpload.step2.columns.area'),
      align: 'center',
      cell: (row) =>
        transitionCell(
          row,
          convertAreaLabel(row.existingAreaCode),
          convertAreaLabel(row.areaCode),
        ),
    },
    {
      key: 'tariffCode',
      header: t('page:cisContractCsvUpload.step2.columns.tariffCode'),
      cell: (row) =>
        transitionCell(row, row.existingCisTariffCode, row.cisTariffCode),
    },
    {
      key: 'voltageClass',
      header: t('page:cisContractCsvUpload.step2.columns.voltageClass'),
      align: 'center',
      cell: (row) =>
        transitionCell(
          row,
          voltageClassLabel(row.existingVoltageClass, t),
          voltageClassLabel(row.voltageClass, t),
        ),
    },
    {
      key: 'ownerBusinessCode',
      header: t('page:cisContractCsvUpload.step2.columns.ownerBusinessCode'),
      // owner_business_code は optional string。unset (帰属未整備) は空欄表示。
      // RowResult 側はすでに resolveOwnerBusinessCode 済みの値 (= 実際に確定時に
      // 保存される値) を返すため、生値をそのまま出せば鉄則1 が保たれる
      // (import.go の doc 参照、issue #6105)。
      // 未設定 (existing 空文字) から初めて登録した CHANGED 行も
      // 「未設定 → 1003」のように変更前後を示す (common.md「変更行は変更前 →
      // 変更後をインライン表示」、codex レビュー指摘)。
      cell: (row) =>
        transitionCell(
          row,
          row.existingOwnerBusinessCode ?? '',
          row.ownerBusinessCode ?? '',
          t('page:cisContractCsvUpload.step2.unsetPlaceholder'),
        ),
    },
    {
      key: 'period',
      header: t('page:cisContractCsvUpload.step2.columns.period'),
      cell: periodCell,
    },
    // 備考列: エラー内容 (common.md「エラー内容を『備考』列に表示する」) と、
    // 契約期間中の削除行への一言 (AC-DUI-019、 design の備考列)。 該当しない行は空。
    {
      key: 'note',
      header: t('page:cisContractCsvUpload.step2.columns.note'),
      cell: (row) => {
        if (row.errorMessages.length > 0) {
          return (
            <CsvDiffErrorText>{row.errorMessages.join(' / ')}</CsvDiffErrorText>
          )
        }
        return row.deletesActiveContract
          ? t('page:cisContractCsvUpload.step2.activeDeleteNote')
          : ''
      },
    },
  ]

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:cisContractCsvUpload.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('common:route.adminUploads'),
          to: routePaths.adminUploads.path,
        },
        { label: t('page:cisContractCsvUpload.breadcrumb.title'), bold: true },
      ]}
    >
      {/* spec `admin/data/uploads/index.md:126` :
          「個別アップロード画面のタイトル下補足も本表の説明文と一致させる。 文言変更は
           本 spec の更新をもって行い、 **実装・プロトタイプ側で独自の説明文を持たない**」。
          そのため取込ハブのカードと**同じ i18n キーを共有**する (別キーに複製すると
          片方だけ直って drift する = issue #4325 で禁じられた状態に戻る)。
          見出しは渡さない — パンくず末尾 (bold) が画面名を兼ねる (PageIntro の docstring)。 */}
      <PageIntro description={t('page:adminUploads.types.cisContract.desc')} />
      {/* 外周 padding は `PageLayout` が包む DS の `SideMenuLayoutContentLayout`
          (`padding="4"` = 16px) が持つ。 ここで更に足すと本画面だけ上・左・右が
          32px になり他画面と揃わない (design `css/layout.css` の
          `.content { padding: 16px }` が正。 デザイナー指摘 2026-08-28)。

          説明文との縦間隔だけは自前で持つ。 `PageIntro` は外側 margin を持たず
          (縦 spacing の所有権を置き場所側へ一本化する方針)、 `PageLayout` も
          children 間に gap を与えないため、 撤去した padding が担っていた 16px が
          消えてステッパーが説明文に接する。 design は説明文側に
          `margin-bottom: var(--space-4)` を置いている (レビュー指摘 2026-08-28)。 */}
      <Box marginTop={4}>
        <Stepper
          currentStep={step}
          stepLabels={[
            t('page:cisContractCsvUpload.stepper.step1'),
            t('page:cisContractCsvUpload.stepper.step2'),
          ]}
        />

        {step === 1 && (
          <CsvUploadStep1Panel
            downloads={[{ onClick: handleTemplateDownload }]}
            framed
            guideSections={[{ rows: cisContractCsvColumnGuide(isEnglish) }]}
            sampleRow={cisContractCsvSampleRow(isEnglish)}
            selectHint={t('page:cisContractCsvUpload.step1.hint')}
          >
            <Dropzone
              onFileSelected={handleFileSelected}
              specText={t('page:cisContractCsvUpload.step1.spec')}
            />

            {precheckError !== undefined && (
              <CompactAlert
                description={t(
                  precheckMessageKey(precheckError.key),
                  precheckError.params,
                )}
                status="error"
                title={t('page:cisContractCsvUpload.step1.syntaxErrorTitle')}
              />
            )}

            {/* 仕様の行との間は design `.up-file-info` の margin-top 16px (gap 12px + 4px) */}
            {fileInfo !== null && <Box marginTop={1}>{fileInfoBox}</Box>}

            <CsvUploadPanelFooter>
              {/* design (`admin/uploads/*.html` `#btn-cancel-step1`) どおり全種別に置く。
                    行き先は取込を始めた画面 (returnTo)、 無ければ CSV 入出力の一覧 */}
              <TertiaryButton
                onClick={() => {
                  void navigate(csvReturn.cancelPath)
                }}
                size="md"
              >
                {t('common:action.cancel')}
              </TertiaryButton>
              {fileInfo !== null && (
                <PrimaryButton onClick={runPreview} size="md">
                  {t('page:cisContractCsvUpload.step1.toStep2')}
                </PrimaryButton>
              )}
            </CsvUploadPanelFooter>
          </CsvUploadStep1Panel>
        )}

        {step === 2 && (
          <CsvDiffPanel
            back={{
              label: t('page:cisContractCsvUpload.step2.backToStep1'),
              onClick: handleBackToStep1,
              disabled: csvReturn.isReturning,
            }}
            confirm={{
              label: t('page:cisContractCsvUpload.step2.confirmOpen'),
              onClick: () => setConfirmStep('commit'),
              // 元の画面へ戻るのを待つ間に、 同じ CSV をもう一度確定させない
              disabled: !canOpenConfirm || csvReturn.isReturning,
            }}
            hint={t('page:cisContractCsvUpload.step2.hint')}
            title={t('page:cisContractCsvUpload.step2.title')}
          >
            {fileInfo !== null && <Box marginBottom={3}>{fileInfoBox}</Box>}

            {isPreviewLoading && (
              <CsvDiffLoading
                columnCount={columns.length}
                label={t('page:cisContractCsvUpload.step2.loading')}
              />
            )}

            {serverErrorMessage !== undefined && (
              <Box marginBottom={3}>
                <CompactAlert status="error" title={serverErrorMessage} />
              </Box>
            )}

            {/* バナー → 件数サマリ → 表 の順は design (`supply-point-master.html`
                #panel-step2) どおり。 */}
            {!isPreviewLoading && hasRowError && (
              <Box marginBottom={3}>
                <CompactAlert
                  status="error"
                  title={t('page:cisContractCsvUpload.step2.errorBannerDesc', {
                    count: summary?.errorRowCount ?? 0,
                  })}
                />
              </Box>
            )}

            {!isPreviewLoading &&
              showNoRowsToProcessBanner &&
              summary !== undefined && (
                <CsvUploadNoopBanner
                  description={t(
                    'page:cisContractCsvUpload.step2.noopBannerDesc',
                    { count: summary.unchangedRowCount },
                  )}
                />
              )}

            {/* AC-DUI-011: 削除 100 行以上で警告バナー。 */}
            {!isPreviewLoading && deleteCount >= DELETE_WARNING_THRESHOLD && (
              <Box marginBottom={3}>
                <CompactAlert
                  description={t(
                    'page:cisContractCsvUpload.step2.deleteWarningDesc',
                  )}
                  status="warning"
                  title={t(
                    'page:cisContractCsvUpload.step2.deleteWarningTitle',
                    {
                      count: deleteCount,
                    },
                  )}
                />
              </Box>
            )}

            {/* AC-DUI-019: 契約期間中の供給地点の削除は件数によらず警告 (AC-DUI-011 とは別に並べる)。 */}
            {!isPreviewLoading && activeDeleteCount > 0 && (
              <Box marginBottom={3}>
                <CompactAlert
                  description={t(
                    'page:cisContractCsvUpload.step2.activeDeleteWarningDesc',
                  )}
                  status="warning"
                  title={t(
                    'page:cisContractCsvUpload.step2.activeDeleteWarningTitle',
                    { count: activeDeleteCount },
                  )}
                />
              </Box>
            )}

            {!isPreviewLoading && summary !== undefined && (
              <SummaryCards
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                summary={summary}
              />
            )}

            {!isPreviewLoading &&
              previewResult !== null &&
              filteredRows.length > PREVIEW_DISPLAY_LIMIT && (
                // total は「現在のフィルタに該当する行数」で、CSV 総行数
                // (summary.totalRowCount) ではない。据え置き行はそもそも
                // row_results に載らないため、CSV 総行数を出すと「打ち切られた」
                // ように読めて実態とずれる。
                <Box marginBottom={3}>
                  <CompactAlert
                    status="info"
                    title={t(
                      'page:cisContractCsvUpload.step2.displayLimitNote',
                      {
                        total: filteredRows.length,
                        limit: PREVIEW_DISPLAY_LIMIT,
                      },
                    )}
                  />
                </Box>
              )}

            {/* 据え置きは row_results に載らない (レスポンスサイズ対策) ため、
                「変更なし」フィルタ選択時は件数のみである旨を明示する。 */}
            {!isPreviewLoading &&
              activeFilter === 'unchanged' &&
              (summary?.unchangedRowCount ?? 0) > 0 && (
                <Box marginBottom={3}>
                  <CompactAlert
                    status="info"
                    title={t(
                      'page:cisContractCsvUpload.step2.unchangedNotListed',
                      { count: summary?.unchangedRowCount ?? 0 },
                    )}
                  />
                </Box>
              )}

            {!isPreviewLoading && previewResult !== null && (
              <CsvDiffTable
                columns={columns}
                getRowKey={(row) => String(row.rowNumber)}
                getRowTone={(row) => ROW_STATUS_TONE[row.status]}
                rows={selectPreviewDisplayRows(filteredRows)}
              />
            )}
          </CsvDiffPanel>
        )}

        <ConfirmDialog
          body={
            <Box>
              {confirmErrorMessage !== undefined && (
                <Alert
                  marginBottom={3}
                  status="error"
                  title={confirmErrorMessage}
                />
              )}
              <Text marginBottom={2}>
                {t('page:cisContractCsvUpload.confirmModal.body')}
              </Text>
              {fileInfo !== null && (
                <Text marginBottom={2} color="Text.midEmphasis" fontSize="sm">
                  {fileInfo.name}
                </Text>
              )}
              {summary !== undefined && (
                <Box
                  marginBottom={2}
                  padding={2}
                  borderWidth="1px"
                  borderColor="Border.lowEmphasis"
                  borderRadius="md"
                >
                  <Text fontSize="sm">
                    {t('page:cisContractCsvUpload.confirmModal.newRowCount', {
                      count: summary.newRowCount,
                    })}
                  </Text>
                  <Text fontSize="sm">
                    {t(
                      'page:cisContractCsvUpload.confirmModal.changedRowCount',
                      { count: summary.changedRowCount },
                    )}
                  </Text>
                  <Text fontSize="sm">
                    {t(
                      'page:cisContractCsvUpload.confirmModal.unchangedRowCount',
                      { count: summary.unchangedRowCount },
                    )}
                  </Text>
                  <Text fontSize="sm">
                    {t(
                      'page:cisContractCsvUpload.confirmModal.deletedRowCount',
                      { count: summary.deletedRowCount },
                    )}
                  </Text>
                  <Text
                    textStyle="captionStrong"
                    marginTop={1}
                    paddingTop={1}
                    borderTopWidth="1px"
                    borderTopColor="Border.lowEmphasis"
                  >
                    {t(
                      'page:cisContractCsvUpload.confirmModal.processedTotal',
                      {
                        count:
                          summary.newRowCount +
                          summary.changedRowCount +
                          summary.unchangedRowCount +
                          summary.deletedRowCount,
                      },
                    )}
                  </Text>
                </Box>
              )}
              {/* AC-DUI-012: 削除 1 行以上で明示的な承認を求める。契約期間中の削除を
                  含む時は AC-DUI-020 の確認ダイアログに代わるので出さない。 */}
              {deleteCount > 0 && activeDeleteCount === 0 && (
                <Text marginTop={2} color="Text.negativeHigh">
                  {t('page:cisContractCsvUpload.confirmModal.deleteConfirm', {
                    count: deleteCount,
                  })}
                </Text>
              )}
            </Box>
          }
          confirmLabel={t('page:cisContractCsvUpload.confirmModal.commit')}
          // AC-DUI-012 の誤削除防止。design/CLAUDE.md「危険性は本文/alert/ボタンで
          // 表現」に従い、削除を含む確定は DangerButton で出す。
          destructive={deleteCount > 0}
          isLoading={importMutation.isPending}
          isOpen={showConfirmModal}
          onCancel={() => {
            setConfirmErrorMessage(undefined)
            setConfirmStep('closed')
          }}
          onConfirm={() => {
            // AC-DUI-020: 契約期間中の削除を含む時は、確定の前に専用の確認を挟む。
            if (activeDeleteCount > 0) {
              setConfirmStep('activeDelete')
              return
            }
            handleCommit()
          }}
          title={t('page:cisContractCsvUpload.confirmModal.title')}
        />

        {/* AC-DUI-020: 「削除」で確定を実行し、「キャンセル」は確定せず確定モーダルへ戻る。 */}
        <ConfirmDialog
          body={
            <Text>
              {t('page:cisContractCsvUpload.activeDeleteModal.body', {
                count: activeDeleteCount,
              })}
            </Text>
          }
          confirmLabel={t(
            'page:cisContractCsvUpload.activeDeleteModal.confirm',
          )}
          destructive
          isOpen={confirmStep === 'activeDelete'}
          onCancel={() => setConfirmStep('commit')}
          onConfirm={() => {
            setConfirmStep('commit')
            handleCommit()
          }}
          title={t('page:cisContractCsvUpload.activeDeleteModal.title')}
        />
      </Box>
    </PageLayout>
  )
}
